import {
    Component,
    EventEmitter,
    Output,
    effect,
    ViewChildren,
    QueryList,
    ElementRef,
    DestroyRef,
    inject,
} from "@angular/core";
import { firstValueFrom, Observable } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import ArcusUserProfile from "../models/arcusUserProfile.model";
import { ArcusLayoutService } from "../services/arcus-layout.service";
import NotificationV2Service from "../services/notification-v2.service";
import { Router, RouterLink } from "@angular/router";
import DisplaySizeService from "../services/displaySize.service";
import { ImpersonateService } from "../services/impersonate.service";
import ExceptionService from "../services/exception.service";
import UserService from "../services/user.service";
import { UserPreferenceService } from "../../SettingsV2/services/user-preference.service";
import { ArcusAuthService } from "../../core/arcusauth.service";
import { ReleaseDialogComponent } from "../../release-dialog/release-dialog.component";
import { IReleaseNote } from "../../interfaces/arcus/ReleaseNote";
import DataService from "../services/data.service";
import { MatDialog } from "@angular/material/dialog";
import ToastService from "../services/toast.service";
import DialogService from "../services/dialog.service";
import { MatToolbar } from "@angular/material/toolbar";
import { MatIconButton } from "@angular/material/button";
import { FaIconComponent, FaLayersComponent } from "@fortawesome/angular-fontawesome";
import { UniversalSearchComponent } from "../universal-search/universal-search.component";
import { MatTooltip } from "@angular/material/tooltip";
import { MatIcon } from "@angular/material/icon";
import { MatBadge } from "@angular/material/badge";
import { MatMenuTrigger, MatMenu, MatMenuItem } from "@angular/material/menu";
import ThemeService from "../services/theme.service";

@Component({
    selector: "arcus-toolbar",
    templateUrl: "./arcus-toolbar.component.html",
    styleUrl: "./arcus-toolbar.component.scss",
    imports: [
        MatToolbar,
        RouterLink,
        MatIconButton,
        FaIconComponent,
        UniversalSearchComponent,
        MatTooltip,
        MatIcon,
        MatBadge,
        MatMenuTrigger,
        FaLayersComponent,
        MatMenu,
        MatMenuItem,
    ],
})
export class ArcusToolbarComponent {
    @Output() logout = new EventEmitter();
    user: ArcusUserProfile;

    get isImpersonating() {
        return this.impersonateService.isImpersonating;
    }

    private destroyRef = inject(DestroyRef);

    constructor(
        private dialogService: DialogService,
        public layoutService: ArcusLayoutService,
        public notificationService: NotificationV2Service,
        public router: Router,
        public displaySizeService: DisplaySizeService,
        private impersonateService: ImpersonateService,
        private exceptionService: ExceptionService,
        private userService: UserService,
        public userPreferenceService: UserPreferenceService,
        private authService: ArcusAuthService,
        private dataService: DataService,
        private dialog: MatDialog,
        private toastr: ToastService,
        private themeService: ThemeService
    ) {
        effect(() => {
            // Skip the placeholder EMPTY_PREFERENCES state (ColorScheme defaults to "system")
            if (!this.userPreferenceService.loaded()) return;
            this.applyColorScheme(this.userPreferenceService.preferences().ColorScheme);
        });

        const media = window.matchMedia("(prefers-color-scheme: dark)");
        const onSystemPreferenceChange = () => {
            if (this.userPreferenceService.preferences().ColorScheme === "system") this.applyColorScheme("system");
        };
        media.addEventListener("change", onSystemPreferenceChange);
        this.destroyRef.onDestroy(() => media.removeEventListener("change", onSystemPreferenceChange));
    }

    private applyColorScheme(choice: string): void {
        const wantsDark =
            choice === "dark" || (choice === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
        const isDark = this.themeService.currentTheme() === "arcusDark";
        if (wantsDark !== isDark) this.themeService.toggleTheme();
    }

    async ngOnInit() {
        this.setUser()
            .then(() => {
                this.userService.getFeatureFlags();
                this.userPreferenceService.load();
            })
            .catch((error) => this.exceptionService.handleException(error))
            .finally(() => {
                this.impersonateService.checkImpersonation();
            });
        const expired = await firstValueFrom(this.authService.isPasswordExpired$);
        if (expired) {
            await this.expiredPassword();
        }
        this.checkForReleaseNotes();
    }

    stopImpersonating() {
        this.impersonateService.stopImpersonating();
    }

    preloadSettingsPreferences() {
        this.userPreferenceService.refresh();
    }

    setUser(): Promise<void> {
        return this.userService
            .currentUser()
            .then((result) => (this.user = result))
            .catch((error) => {
                this.exceptionService.handleException(error);
                return (this.user = undefined);
            });
    }

    checkForReleaseNotes() {
        this.dataService
            .getApi("ReleaseNotes")
            .then((result: IReleaseNote[]) => {
                if (result?.length > 0) {
                    this.dialog
                        .open(ReleaseDialogComponent, {
                            minWidth: "550px",
                            minHeight: "75vh",
                            maxHeight: "100vh",
                            maxWidth: "750px",
                            disableClose: true,
                            data: {
                                releaseNotes: result,
                            },
                        })
                        .afterClosed()
                        .subscribe((result) => {
                            if (!result?.dontShowAgain)
                                this.dataService.postApi(`ReleaseNotes/${result?.releaseNumber}`, null);
                            else
                                this.dataService.postApi(
                                    `ReleaseNotes/${result?.releaseNumber}?dontShow=${result?.dontShowAgain}`,
                                    null
                                );
                        });
                }
            })
            .catch((error) => this.exceptionService.handleException(error));
    }

    async expiredPassword() {
        await this.dialogService
            .mdAlert(
                "Password has expired, and must be changed. Please click 'Change Password' and then check your inbox for next steps.",
                "Password Expired",
                "Change Password",
                "key",
                true
            )
            .afterClosed()
            .subscribe(async () => {
                try {
                    await this.authService.changeRequest();
                    this.authService.logout();
                    this.toastr.success("Password reset email has been sent. Please check your inbox.");
                } catch (err) {
                    console.error("Failed to send password reset email:", err);
                    this.toastr.error("Failed to send password reset email. Please try again later.");
                }
            });
    }

    supportBtn() {
        const baseUrl = "https://arcus.atlassian.net/servicedesk/customer/portal/1/group/1/create/108";

        const roles = this.user?.Roles?.map((r) => r.RoleName).join(", ") ?? "N/A";
        const { browserName, browserVersion, os } = this.getClientInfo();

        const customfield_10227 = [
            `*User:* ${this.user?.FullName ?? "N/A"} (${this.user?.Email ?? "N/A"})`,
            `*Branch ID:* ${this.user?.PrimaryBranchID ?? "N/A"}`,
            `*Roles:* ${roles}`,
            `*Current Page:* ${window.location.href}`,
            `----`,
            `*OS:* ${os}`,
            `*Browser:* ${browserName} ${browserVersion}`,
            `*Screen:* ${window.screen.width}x${window.screen.height}`,
            `*Timezone:* ${Intl.DateTimeFormat().resolvedOptions().timeZone}`,
            `*Language:* ${navigator.language}`,
        ].join("\n");

        const params = new URLSearchParams({ customfield_10227 });
        window.open(`${baseUrl}?${params.toString()}`, "_blank");
    }

    private getClientInfo(): { browserName: string; browserVersion: string; os: string } {
        const ua = navigator.userAgent;
        let browserName = "Unknown";
        let browserVersion = "Unknown";
        let os = "Unknown";

        if (/Windows NT/.test(ua)) {
            const match = ua.match(/Windows NT ([\d.]+)/);
            const winVersionMap: Record<string, string> = { "10.0": "10/11", "6.3": "8.1", "6.2": "8", "6.1": "7" };
            os = `Windows ${winVersionMap[match?.[1] ?? ""] ?? match?.[1] ?? ""}`;
        } else if (/Mac OS X/.test(ua)) {
            const match = ua.match(/Mac OS X ([\d_]+)/);
            os = `macOS ${match?.[1]?.replace(/_/g, ".") ?? ""}`;
        } else if (/Android/.test(ua)) {
            const match = ua.match(/Android ([\d.]+)/);
            os = `Android ${match?.[1] ?? ""}`;
        } else if (/iPhone|iPad/.test(ua)) {
            os = "iOS";
        } else if (/Linux/.test(ua)) {
            os = "Linux";
        }

        // Order matters: Edge and Opera both include "Chrome" in their UA string
        if (/Edg\//.test(ua)) {
            browserName = "Edge";
            browserVersion = ua.match(/Edg\/([\d.]+)/)?.[1] ?? "";
        } else if (/OPR\//.test(ua)) {
            browserName = "Opera";
            browserVersion = ua.match(/OPR\/([\d.]+)/)?.[1] ?? "";
        } else if (/Firefox\//.test(ua)) {
            browserName = "Firefox";
            browserVersion = ua.match(/Firefox\/([\d.]+)/)?.[1] ?? "";
        } else if (/Chrome\//.test(ua)) {
            browserName = "Chrome";
            browserVersion = ua.match(/Chrome\/([\d.]+)/)?.[1] ?? "";
        } else if (/Version\//.test(ua) && /Safari\//.test(ua)) {
            browserName = "Safari";
            browserVersion = ua.match(/Version\/([\d.]+)/)?.[1] ?? "";
        }

        return { browserName, browserVersion, os };
    }
}

